import React from "react";
import PlatformModal from "components/layout/PlatformModal";
import useAppLanguage from "hooks/useAppLanguage";

const APP_VERSION = "1.0.0";
const APP_YEAR = new Date().getFullYear();
const APP_WEBSITE = "https://fxbazzar.com";

const PLATFORM_STACK = [
  "React 18",
  "Redux Toolkit",
  "Lightweight Charts",
  "Socket.IO live feed",
  "FastTrade REST API",
];

/** Shared About content — modal (desktop) or full page (mobile Settings). */
export function AboutProgramContent({ onClose, asPage = false }) {
  const { t } = useAppLanguage();
  const features = [
    { icon: "📈", label: t("aboutMw"), desc: t("aboutMwDesc") },
    { icon: "🕯️", label: t("aboutCharts"), desc: t("aboutChartsDesc") },
    { icon: "⚡", label: t("aboutTradeOrders"), desc: t("aboutTradeOrdersDesc") },
    { icon: "💼", label: t("wallet"), desc: t("aboutWalletDesc") },
    { icon: "📊", label: t("reports"), desc: t("aboutReportsDesc") },
    { icon: "🔐", label: t("aboutAccounts"), desc: t("aboutAccountsDesc") },
  ];
  const markets = [
    t("nseFutures"),
    t("nseOptions"),
    t("mcxCommodities"),
    t("indexNifty"),
    t("giftNifty"),
  ];
  const notes = [t("aboutNote1"), t("aboutNote2"), t("aboutNote3")];

  return (
    <div className={`platform-about${asPage ? " platform-about--page" : ""}`}>
      <div className="platform-about__hero">
        {!asPage ? (
          <button
            type="button"
            className="platform-about__close"
            onClick={onClose}
            aria-label={t("close")}
          >
            ✕
          </button>
        ) : null}
        <div className="platform-about__brand">
          <div className="platform-about__logo" aria-hidden>
            <span>FX</span>
          </div>
          <div className="platform-about__brand-text">
            <h2 className="platform-about__title">FxBazzar</h2>
            <p className="platform-about__subtitle">{t("mobileTradingTerminal")}</p>
          </div>
        </div>
        <span className="platform-about__version-badge">{t("version")} {APP_VERSION}</span>
      </div>

      <div className="platform-about__body">
        <p className="platform-about__tagline">{t("aboutTagline")}</p>

        <div className="platform-about__section">
          <h3 className="platform-about__section-title">{t("whatYouCanDo")}</h3>
          <ul className="platform-about__features">
            {features.map((f) => (
              <li key={f.label} className="platform-about__feature">
                <span className="platform-about__feature-icon" aria-hidden>{f.icon}</span>
                <span className="platform-about__feature-text">
                  <strong>{f.label}</strong>
                  <span>{f.desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="platform-about__section">
          <h3 className="platform-about__section-title">{t("marketsAndSegments")}</h3>
          <div className="platform-about__tags">
            {markets.map((tag) => (
              <span key={tag} className="platform-about__tag">{tag}</span>
            ))}
          </div>
        </div>

        <div className="platform-about__section">
          <h3 className="platform-about__section-title">{t("aboutPlatform")}</h3>
          <ul className="platform-about__notes">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>

        <div className="platform-about__section">
          <h3 className="platform-about__section-title">{t("technology")}</h3>
          <div className="platform-about__tags">
            {PLATFORM_STACK.map((tag) => (
              <span key={tag} className="platform-about__tag">{tag}</span>
            ))}
          </div>
        </div>

        <p className="platform-about__disclaimer">{t("aboutDisclaimer")}</p>

        <div className="platform-about__meta">
          <span>
            {t("website")}:{" "}
            <a
              href={APP_WEBSITE}
              target="_blank"
              rel="noopener noreferrer"
              className="platform-about__link"
            >
              fxbazzar.com
            </a>
          </span>
          <span>{t("aboutLiveData")}</span>
          <span className="platform-about__copyright">
            © {APP_YEAR} FxBazzar. {t("allRightsReserved")}
          </span>
        </div>
      </div>

      {!asPage ? (
        <div className="platform-about__footer">
          <button type="button" className="btn-confirm platform-about__done" onClick={onClose}>
            {t("close")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function AboutProgramModal({ onClose, darkTheme = false }) {
  return (
    <PlatformModal
      onClose={onClose}
      darkTheme={darkTheme}
      boxClassName="platform-about-modal"
      boxStyle={{ maxWidth: 480, maxHeight: "min(88dvh, 920px)" }}
    >
      <AboutProgramContent onClose={onClose} />
    </PlatformModal>
  );
}
